import React, { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { derivePricingStrategy } from '../utils/derivePricingStrategy.ts';
import { AutomaticProductsFormProps } from '@/shared/forms/AutomaticProductsForm/AutomaticProductsForm.tsx';
import { mapAutomaticProductsFormValues } from '../domain/mapAutomaticProductsFormValues.ts';
import { isMissingLocationWithWeighingData } from '../domain/isWeighingLocationRequired.ts';

export function useFormSubmit(
    methods: UseFormReturn<AutomaticProductsFormValues>,
    onSubmit: AutomaticProductsFormProps['onSubmit']
) {
    const { handleSubmit, watch } = methods;

    const [
        productType,
        isSalesTransportIncluded,
        isPurchaseTransportIncluded,
        salesMaterialUnit,
        salesTransportUnit,
        purchaseMaterialUnit,
        purchaseTransportUnit,
        additionalServices,
        loadingLocation,
        unloadingLocation,
    ] = watch([
        'productType',
        'isSalesTransportIncluded',
        'isPurchaseTransportIncluded',
        'salesMaterialUnit',
        'salesTransportUnit',
        'purchaseMaterialUnit',
        'purchaseTransportUnit',
        'additionalServices',
        'loadingLocation',
        'unloadingLocation',
    ]);

    const isSubmitDisabled = isMissingLocationWithWeighingData({
        productType,
        isSalesTransportIncluded,
        isPurchaseTransportIncluded,
        salesMaterialUnit,
        salesTransportUnit,
        purchaseMaterialUnit,
        purchaseTransportUnit,
        additionalServices,
        loadingLocation,
        unloadingLocation,
    });

    const handleFormSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
        async event => {
            event.preventDefault();
            await handleSubmit(values => {
                onSubmit({
                    values,
                    businessCase: {
                        productType: values.productType,
                        isStandalone: values.isStandalone,
                        pricingStrategy: derivePricingStrategy(
                            values.isSalesTransportIncluded,
                            values.isPurchaseTransportIncluded
                        ),
                    },
                    productConfigurations: mapAutomaticProductsFormValues(values),
                });
            }, console.error)();
        },
        [handleSubmit, onSubmit]
    );

    return { handleFormSubmit, isSubmitDisabled };
}
